import React, { useEffect, useState } from "react";
import { X, Send, ShieldCheck, Key, Copy, Check, AlertCircle } from "lucide-react";
import { fetchProfile } from "../../Services/profile/profileservice";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { changeAPIKeyGetOTP, changeAPIKeyVerifyOTP } from "../../Services/AddBankAC.js/IpService";

function APIKeyChangeModal({ isOpen, onClose }) {
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Flow steps: 1 = Send OTP, 2 = Verify OTP, 3 = Success View
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [verificationResponse, setVerificationResponse] = useState(null);
  const [copied, setCopied] = useState(false);

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      }
    };
    if (isOpen) {
      loadProfile();
      // Reset state when modal opens
      setStep(1);
      setOtp("");
      setIdentifier("");
      setVerificationResponse(null);
    }
  }, [isOpen]);

  const handleSendOTP = async () => {
    if (!profileData) {
      showErrorToast("Profile data not loaded yet.");
      return;
    }

    try {
      setIsLoading(true);

      const res = await changeAPIKeyGetOTP({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
      });

      if (res?.status?.code === 200 || res?.status?.type === "success") {
        showSuccessToast(res?.message || res?.data?.message || "OTP sent successfully.");
        setIdentifier(res?.data?.identifier);
        setStep(2); // Move to OTP input step
      } else {
        showErrorToast(res?.message || res?.data?.message || "Failed to send OTP.");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length < 4) {
      showErrorToast("Please enter a valid OTP.");
      return;
    }

    try {
      setIsLoading(true);

      const res = await changeAPIKeyVerifyOTP({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        identifier,
        otp,
      });

      // Based on provided JSON, success response has status: true
      if (res?.status === true || res?.status?.code === 200) {
        setVerificationResponse(res?.data);
        showSuccessToast(res?.message || "API key changed successfully.");
        setStep(3); // Move to Success Data Display
      } else {
        showErrorToast(res?.message || res?.data?.message || "Invalid OTP.");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showSuccessToast("API Key copied to clipboard!");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative flex flex-col">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2 text-gray-800">
            <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
              <Key size={20} />
            </div>
            <h2 className="text-lg font-semibold">Change API Key</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          {/* STEP 1: Send OTP */}
          {step === 1 && (
            <div className="flex flex-col items-center text-center gap-4 py-4">
              <div className="bg-blue-50 p-4 rounded-full mb-2">
                <AlertCircle className="w-10 h-10 text-blue-500" />
              </div>
              <h3 className="text-gray-800 font-medium text-lg">
                Security Verification
              </h3>
              <p className="text-sm text-gray-500 mb-4 px-2">
                To generate a new API key, we need to verify your identity. An OTP will be sent to your registered mobile number.
              </p>
              <button
                onClick={handleSendOTP}
                disabled={isLoading || !profileData}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 px-4 rounded-xl transition-all shadow-md hover:shadow-lg disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="animate-spin border-2 border-white/30 border-t-white rounded-full w-5 h-5" />
                ) : (
                  <>
                    <Send size={18} />
                    Send OTP to Registered Mobile
                  </>
                )}
              </button>
            </div>
          )}

          {/* STEP 2: Verify OTP */}
          {step === 2 && (
            <div className="flex flex-col gap-5 py-2 animate-in slide-in-from-right-4 duration-300">
              <div className="text-center mb-2">
                <h3 className="text-gray-800 font-medium text-lg mb-1">Enter OTP</h3>
                <p className="text-sm text-gray-500">
                  Please enter the verification code sent to your mobile.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  maxLength={6}
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-center text-xl tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  autoFocus
                />
              </div>
              <button
                onClick={handleVerifyOTP}
                disabled={isLoading || otp.length < 4}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 px-4 rounded-xl transition-all shadow-md hover:shadow-lg disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="animate-spin border-2 border-white/30 border-t-white rounded-full w-5 h-5" />
                ) : (
                  <>
                    <ShieldCheck size={18} />
                    Verify & Change API Key
                  </>
                )}
              </button>
            </div>
          )}

          {/* STEP 3: Success Data Display */}
          {step === 3 && verificationResponse && (
            <div className="flex flex-col gap-4 animate-in zoom-in-95 duration-300">
              <div className="flex flex-col items-center text-center mb-2">
                <div className="bg-green-100 p-3 rounded-full mb-3 text-green-600">
                  <Check size={28} />
                </div>
                <h3 className="text-gray-800 font-semibold text-lg">Success!</h3>
                <p className="text-sm text-gray-500">
                  Your API Key has been successfully generated.
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-3 text-sm">
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-500">User ID</span>
                  <span className="font-medium text-gray-800">{verificationResponse.user_id}</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-500">User Name</span>
                  <span className="font-medium text-gray-800">{verificationResponse.user_name}</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-500">API Key ID</span>
                  <span className="font-medium text-gray-800">{verificationResponse.api_key_id}</span>
                </div>
                <div className="flex justify-between items-center pb-1">
                  <span className="text-gray-500">Changed At</span>
                  <span className="font-medium text-gray-800">{verificationResponse.changed_at}</span>
                </div>
              </div>

              <div className="mt-2">
                <label className="block text-xs font-medium text-gray-500 mb-1 ml-1 uppercase tracking-wider">
                  New API Key
                </label>
                <div className="flex items-center justify-between bg-gray-900 text-gray-100 rounded-xl p-3 border border-gray-800">
                  <code className="text-sm truncate mr-2 select-all font-mono">
                    {verificationResponse.api_key}
                  </code>
                  <button
                    onClick={() => copyToClipboard(verificationResponse.api_key)}
                    className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 transition-colors flex-shrink-0"
                    title="Copy API Key"
                  >
                    {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-full mt-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-3 px-4 rounded-xl transition-colors"
              >
                Close & Continue
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default APIKeyChangeModal;